import React, { useEffect, useMemo, useRef, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { ProductCard } from "./ProductCard";

// Window-based virtual scroll grid. Renders rows of products based on screen width.
export const VirtualProductGrid = ({ products, onBuy, onOpen }) => {
  const parentRef = useRef(null);
  const [cols, setCols] = useState(2);

  useEffect(() => {
    const compute = () => {
      const w = window.innerWidth;
      if (w >= 1280) setCols(4);
      else if (w >= 1024) setCols(3);
      else if (w >= 640) setCols(2);
      else setCols(2);
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, []);

  const rows = useMemo(() => {
    const out = [];
    for (let i = 0; i < products.length; i += cols) {
      out.push(products.slice(i, i + cols));
    }
    return out;
  }, [products, cols]);

  // Approximate row height: card aspect 4:5 + text area. Card width ~ (containerWidth - gaps) / cols.
  const [rowHeight, setRowHeight] = useState(440);
  useEffect(() => {
    const compute = () => {
      const cw = parentRef.current?.clientWidth || window.innerWidth;
      const gap = 24;
      const cardW = (cw - gap * (cols - 1)) / cols;
      const imgH = cardW * (5 / 4);
      setRowHeight(Math.round(imgH + 170)); // image + content + gap
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, [cols]);

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => rowHeight,
    overscan: 4,
  });

  return (
    <div
      ref={parentRef}
      data-testid="home-product-grid"
      className="w-full overflow-auto"
      style={{ height: "min(1200px, calc(100vh - 80px))" }}
    >
      <div
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
          width: "100%",
          position: "relative",
        }}
      >
        {rowVirtualizer.getVirtualItems().map((vRow) => {
          const row = rows[vRow.index];
          return (
            <div
              key={vRow.key}
              data-index={vRow.index}
              ref={rowVirtualizer.measureElement}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                transform: `translateY(${vRow.start}px)`,
              }}
              className="pb-6"
            >
              <div
                className="grid gap-6"
                style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
              >
                {row.map((p) => (
                  <ProductCard
                    key={p.id}
                    product={p}
                    onBuy={onBuy}
                    onOpen={onOpen}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
